import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { User, Mail, Car, Hash, KeyRound, Phone, Loader2 } from 'lucide-react';
import { markDriverSignedIn, API } from '../lib/driverAuth';
import PhoneInput from '../components/ui/phone-input';

const Field = ({ label, icon: Icon, children }) => (
  <div>
    <label className="block text-[11px] uppercase tracking-[0.18em] text-neutral-500 mb-2">
      <span className="inline-flex items-center gap-1.5"><Icon className="h-3.5 w-3.5" /> {label}</span>
    </label>
    {children}
  </div>
);

const txt = "w-full h-11 px-4 rounded-md border border-neutral-300 bg-white text-black text-base";
const pinClass = "w-full h-11 px-4 rounded-md border border-neutral-300 bg-white text-black text-lg tracking-[0.4em] text-center font-mono";

const DriverSignup = () => {
  const navigate = useNavigate();
  const [f, setF] = useState({
    name: '', phone: '', email: '', vehicleModel: '', vehicleReg: '',
    pin: '', confirmPin: '',
  });
  const set = (k) => (v) => setF((prev) => ({ ...prev, [k]: typeof v === 'string' ? v : v.target.value }));
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.phone || !f.email || !f.pin) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (f.pin.length < 4 || f.pin.length > 6) {
      toast.error('PIN must be 4-6 digits');
      return;
    }
    if (f.pin !== f.confirmPin) {
      toast.error('PINs do not match');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/driver/auth/signup`, {
        name: f.name.trim(),
        phone: f.phone,
        email: f.email.trim().toLowerCase(),
        pin: f.pin,
        vehicleModel: f.vehicleModel || null,
        vehicleReg: f.vehicleReg || null,
      }, { withCredentials: true });
      markDriverSignedIn(true);
      toast.success(`Welcome to Obsidian, ${data.driver?.name?.split(' ')[0]}`);
      navigate('/driver', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sign-up failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-5 py-12">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <p className="text-white/40 uppercase tracking-[0.4em] text-xs mb-3">Obsidian</p>
          <h1 className="text-white text-3xl font-bold tracking-tight">Driver Sign-Up</h1>
          <p className="text-white/50 text-sm mt-2">Create your driver account</p>
        </div>

        <form onSubmit={submit} className="bg-white rounded-3xl p-6 shadow-2xl space-y-4" data-testid="driver-signup-form">
          <Field label="Full Name *" icon={User}>
            <input type="text" value={f.name} onChange={set('name')} className={txt} data-testid="ds-name" autoComplete="name" />
          </Field>
          <Field label="Phone *" icon={Phone}>
            <PhoneInput value={f.phone} onChange={set('phone')} className="bg-white border-neutral-300 text-black h-11" data-testid="ds-phone" />
          </Field>
          <Field label="Email *" icon={Mail}>
            <input type="email" value={f.email} onChange={set('email')} className={txt} data-testid="ds-email" autoComplete="email" />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Vehicle Model" icon={Car}>
              <input type="text" value={f.vehicleModel} onChange={set('vehicleModel')} className={txt} placeholder="E-Class" data-testid="ds-vehicle" />
            </Field>
            <Field label="Vehicle Reg" icon={Hash}>
              <input type="text" value={f.vehicleReg} onChange={set('vehicleReg')} className={txt} placeholder="AB12 CDE" data-testid="ds-reg" />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="PIN *" icon={KeyRound}>
              <input type="password" inputMode="numeric" maxLength={6} value={f.pin}
                onChange={(e) => set('pin')(e.target.value.replace(/\D/g, ''))}
                className={pinClass} placeholder="••••" data-testid="ds-pin" />
            </Field>
            <Field label="Confirm PIN *" icon={KeyRound}>
              <input type="password" inputMode="numeric" maxLength={6} value={f.confirmPin}
                onChange={(e) => set('confirmPin')(e.target.value.replace(/\D/g, ''))}
                className={pinClass} placeholder="••••" data-testid="ds-pin-confirm" />
            </Field>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full h-12 rounded-md bg-black text-white font-semibold hover:bg-neutral-900 disabled:opacity-60 flex items-center justify-center gap-2"
            data-testid="ds-submit"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {submitting ? 'Creating account…' : 'Create Account'}
          </button>

          <p className="text-center text-xs text-neutral-500 pt-1">
            Already have an account?{' '}
            <Link to="/driver/login" className="text-black underline underline-offset-2" data-testid="ds-go-login">
              Sign in
            </Link>
          </p>
        </form>

        <p className="text-center text-[11px] text-white/30 mt-6 uppercase tracking-wider">
          Confidential · Authorised drivers only
        </p>
      </div>
    </div>
  );
};

export default DriverSignup;
